'use strict';

/**
 * Profile -- a user's personal page: a moodboard banner up top, a stack of open-ended content blocks
 * (text, image, embed) below it, and their rendered About. Your own page edits in place (the board is
 * a live moodboard editor, blocks have add / reorder / delete / edit affordances); a teammate's page
 * is read-only.
 *
 * The record to show is at AppData.ProfileView.Record (self from UserAPI.getMe; a teammate from
 * TeamAPI.getMember). The board is a Moodboard row owned by the user (MoodboardAPI personal board);
 * the blocks are ProfileBlock rows (ProfileBlockAPI). Two registered moodboard views back the banner:
 * Profile-Board-Edit (editable, autosaving) for self and Profile-Board-View (read-only) for teammates.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');
const libAvatarHelper = require('./AvatarHelper.js');
const libMarkdownRender = require('./MarkdownRender.js');

const _BOARD_EDIT = 'Profile-Board-Edit';
const _BOARD_VIEW = 'Profile-Board-View';
const _AUTOSAVE_MS = 800;

function _escapeHTML(pStr)
{
	return String(pStr == null ? '' : pStr)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Turn a watch/share URL into an embeddable one for the common video hosts; otherwise return null so
// the block falls back to a plain link card (we never drop an arbitrary URL into an iframe).
function _embedURL(pURL)
{
	let tmpURL = String(pURL || '').trim();
	if (!/^https:\/\//i.test(tmpURL)) { return null; }
	let tmpYouTube = tmpURL.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
	if (tmpYouTube) { return 'https://www.youtube.com/embed/' + tmpYouTube[1]; }
	let tmpVimeo = tmpURL.match(/vimeo\.com\/(\d+)/);
	if (tmpVimeo) { return 'https://player.vimeo.com/video/' + tmpVimeo[1]; }
	return null;
}

class PictViewProfile extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this.pict.AppData.ProfileView = this.pict.AppData.ProfileView || { Record: null, Blocks: [], BoardID: null };
		this._SaveTimer = null;
	}

	_state() { return this.pict.AppData.ProfileView; }

	_record()
	{
		let tmpRecord = this._state() && this._state().Record;
		if (tmpRecord) { return tmpRecord; }
		return (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
	}

	_isSelf(pRecord)
	{
		let tmpMe = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
		let tmpRec = pRecord || this._record();
		return !!(tmpMe.IDUser && tmpRec.IDUser && tmpMe.IDUser === tmpRec.IDUser);
	}

	// ── entry point: render the page, then load the board + blocks ───────────

	show()
	{
		// A nav token: each show() is a fresh page load, so async board/block fetches from an earlier
		// navigation are ignored when they land (the two board views share one container, so a stale
		// load must not render the wrong one).
		this._NavToken = (this._NavToken || 0) + 1;
		this.render();
		this._loadBoard(this._NavToken);
		this._loadBlocks(this._NavToken);
	}

	_loadBoard(pToken)
	{
		let tmpRecord = this._record();
		let tmpAPI = this.pict.providers.MoodboardAPI;
		if (!tmpAPI || !tmpRecord.IDUser) { return; }
		// Clear the banner up front: two board views (Edit/View) share #Profile-Board, so a render left
		// by a previous navigation must not linger if this load is superseded or chooses the other view.
		if (typeof document !== 'undefined') { let tmpHost = document.getElementById('Profile-Board'); if (tmpHost) { tmpHost.innerHTML = ''; } }

		let tmpSelf = this._isSelf(tmpRecord);
		let tmpPromise = tmpSelf ? tmpAPI.getOrCreatePersonal(tmpRecord.IDUser) : tmpAPI.listForUser(tmpRecord.IDUser).then((pRows) => (pRows && pRows[0]) || null);
		tmpPromise.then((pBoardRow) =>
		{
			if (pToken && pToken !== this._NavToken) { return; }
			// Recompute self at render time (the record may have settled after this load was kicked off).
			let tmpSelf = this._isSelf();
			this._state().BoardID = (pBoardRow && pBoardRow.IDMoodboard) ? pBoardRow.IDMoodboard : null;
			let tmpBoard = pBoardRow ? tmpAPI.loadBoard(pBoardRow) : { Nodes: [], Connections: [], ViewState: { PanX: 0, PanY: 0, Zoom: 1 } };
			this._state().Board = tmpBoard;
			this._state().BoardEditing = false;
			let tmpHasNodes = !!(tmpBoard && tmpBoard.Nodes && tmpBoard.Nodes.length);
			this._presentBoard(tmpBoard, tmpHasNodes, tmpSelf, pToken);
		});
	}

	// Decide how the board banner shows: a teammate's empty board is hidden (no big empty block); your
	// own empty board shows a slim "decorate" prompt; a board with cards renders normally.
	_presentBoard(pBoard, pHasNodes, pSelf, pToken)
	{
		if (typeof document === 'undefined') { return; }
		let tmpBanner = document.getElementById('Profile-Board');
		let tmpPrompt = document.getElementById('Profile-Board-Empty');
		// An undecorated teammate board: hide the whole banner (the header is its own section, so nothing
		// to adjust -- it just sits at the top).
		if (!pHasNodes && !pSelf)
		{
			if (tmpBanner) { tmpBanner.style.display = 'none'; }
			if (tmpPrompt) { tmpPrompt.style.display = 'none'; }
			return;
		}
		// Your own empty board: a slim "decorate" prompt (which drops you into edit).
		if (!pHasNodes && pSelf)
		{
			if (tmpBanner) { tmpBanner.style.display = 'none'; }
			if (tmpPrompt) { tmpPrompt.style.display = 'flex'; }
			return;
		}
		// A board with content shows read-only for everyone (the way teammates see it), in its saved display
		// style. On your own board an "Edit board" button sits next to "Edit profile" (see _renderActions);
		// a teammate's does not.
		if (tmpPrompt) { tmpPrompt.style.display = 'none'; }
		if (tmpBanner) { tmpBanner.style.display = ''; }
		this._mountBoard(this.pict.views[_BOARD_VIEW], pBoard, pToken);
	}

	// Flip the banner between read-only (the saved display style) and editing (the free canvas), driven by
	// the "Edit board" / "Done" button next to "Edit profile". Editing happens inline on the editable
	// instance; the moodboard's own toolbar carries the style toggle + set-view-area while editing.
	toggleBoardEdit()
	{
		if (!this._isSelf()) { return; }
		if (this._state().BoardEditing) { this._exitBoardEdit(); } else { this._enterBoardEdit(); }
	}

	// Update the "Edit board" / "Done" label to match the current board mode.
	_updateEditBoardButton()
	{
		let tmpButton = (typeof document !== 'undefined') ? document.getElementById('Profile-Edit-Board') : null;
		if (tmpButton) { tmpButton.textContent = this._state().BoardEditing ? 'Done' : 'Edit board'; }
	}

	// Mount a board into #Profile-Board. The moodboard flow needs a SECOND render pass on a cold mount
	// before its cards paint (the first pass only stands up the node-view machinery -- this is why a
	// board was blank the first visit and fine the second). Render, render again a couple of frames
	// later, then set the board, re-checking the nav token at each step.
	_mountBoard(pBoardView, pBoard, pToken)
	{
		let tmpSelf = this;
		this._ProfileBoardViewName = pBoardView.options.ViewIdentifier;
		pBoardView.render();
		this._afterFrames(2, function ()
		{
			if (pToken && pToken !== tmpSelf._NavToken) { return; }
			pBoardView.render();
			tmpSelf._afterFrames(2, function ()
			{
				if (pToken && pToken !== tmpSelf._NavToken) { return; }
				pBoardView.setBoard(pBoard);
			});
		});
	}

	// ── board display style (canvas / jumbotron / background) ────────────────────
	// The display-style toggle + set-view-area live in the moodboard's own edit toolbar; the board color +
	// backdrop margin in its gear (no host chrome). The host's only job is to lay out the board for the
	// current presentation, which the moodboard reports via onDisplayStyleChanged: a jumbotron is a hero
	// band (its scaled frame height) above the content; a background becomes a full-bleed layer BEHIND the
	// whole profile column (the head / blocks / About render on top); the canvas is the normal banner.
	onBoardDisplayStyleChanged(pInfo)
	{
		let tmpEl = (typeof document !== 'undefined') ? document.getElementById('Profile-Board') : null;
		if (!tmpEl || !pInfo) { return; }
		let tmpProfile = (typeof tmpEl.closest === 'function') ? tmpEl.closest('.cst-profile') : null;
		let tmpInner = tmpProfile ? tmpProfile.querySelector('.cst-profile-inner') : null;
		tmpEl.classList.remove('cst-board-jumbotron', 'cst-board-background');
		tmpEl.style.background = '';
		if (tmpInner) { tmpInner.style.marginTop = ''; }
		if (tmpProfile) { tmpProfile.classList.remove('cst-profile-backdrop'); }
		if (pInfo.effectiveStyle === 'jumbotron')
		{
			// A hero band; its height is the moodboard's reported jumbotronHeight (the gear "Band height"
			// override, else the scaled view-area frame height), clamped to a sane banner range.
			tmpEl.classList.add('cst-board-jumbotron');
			let tmpHeight = pInfo.jumbotronHeight;
			tmpEl.style.height = (tmpHeight > 0 ? Math.max(80, Math.min(1000, tmpHeight)) : 240) + 'px';
		}
		else if (pInfo.effectiveStyle === 'background')
		{
			// Backdrop: the board is a full-bleed layer behind the content (cst-profile-backdrop CSS), no
			// fixed band height -- it spans the column. The gear margin is WHERE THE OVERLAYING CONTENT
			// STARTS, so push the content down by it (the board art shows in the band above). Paint the
			// resolved board color on the board element here (the backdrop CSS makes the flow containers
			// transparent over it) because the flow's own SVG fill does not composite reliably as an
			// absolute full-bleed layer.
			tmpEl.classList.add('cst-board-background');
			tmpEl.style.height = '';
			tmpEl.style.background = pInfo.backgroundColor || '';
			if (tmpInner) { tmpInner.style.marginTop = (pInfo.topMargin > 0 ? pInfo.topMargin : 0) + 'px'; }
			if (tmpProfile) { tmpProfile.classList.add('cst-profile-backdrop'); }
		}
		else
		{
			tmpEl.style.height = '';
		}
	}

	// The "decorate your board" prompt button (your own empty board): reveal the editable board so you
	// can start adding cards.
	startDecorating()
	{
		if (!this._isSelf() || typeof document === 'undefined') { return; }
		let tmpPrompt = document.getElementById('Profile-Board-Empty');
		let tmpBanner = document.getElementById('Profile-Board');
		if (tmpPrompt) { tmpPrompt.style.display = 'none'; }
		if (tmpBanner) { tmpBanner.style.display = ''; tmpBanner.innerHTML = ''; }
		let tmpEmpty = { Nodes: [], Connections: [], ViewState: { PanX: 0, PanY: 0, Zoom: 1 } };
		this._state().Board = tmpEmpty;
		this._state().BoardEditing = true;
		let tmpView = this.pict.views[_BOARD_EDIT];
		if (!tmpView) { return; }
		this._mountBoard(tmpView, tmpEmpty, this._NavToken);
		this._updateEditBoardButton();
	}

	// Entered from the "Edit board" button (next to Edit profile) or a card double-click. Mounts the
	// editable board inline (the moodboard's own toolbar carries the editing controls); the board's own
	// fullscreen button is there if you want more room.
	_enterBoardEdit()
	{
		if (!this._isSelf()) { return; }
		// Show the banner and hide the empty "decorate" prompt -- you are decorating now. Entering edit from
		// the "Edit board" button (rather than the prompt's own "Decorate" button, which already hides it)
		// must clear the prompt too, or it sits under the editable canvas and lingers after you add a card.
		if (typeof document !== 'undefined')
		{
			let tmpBanner = document.getElementById('Profile-Board'); if (tmpBanner) { tmpBanner.style.display = ''; tmpBanner.innerHTML = ''; }
			let tmpPrompt = document.getElementById('Profile-Board-Empty'); if (tmpPrompt) { tmpPrompt.style.display = 'none'; }
		}
		this._state().BoardEditing = true;
		this._mountBoard(this.pict.views[_BOARD_EDIT], this._state().Board || { Nodes: [], Connections: [], ViewState: { PanX: 0, PanY: 0, Zoom: 1 } }, this._NavToken);
		this._updateEditBoardButton();
	}

	// A double-click on a card of your own (read-only) board: drop into edit and open that card's panel,
	// so you can edit a card without first toggling edit mode. Polls until the edit board has the node.
	activateCard(pNodeHash)
	{
		if (!this._isSelf() || !pNodeHash) { return; }
		if (!this._state().BoardEditing) { this._enterBoardEdit(); }
		let tmpEdit = this.pict.views[_BOARD_EDIT];
		let tmpTries = 0;
		let fOpen = function ()
		{
			let tmpFlow = tmpEdit && tmpEdit._FlowView;
			if (tmpFlow && tmpFlow.initialRenderComplete && typeof tmpFlow.getNode === 'function' && tmpFlow.getNode(pNodeHash) && typeof tmpFlow.openPanel === 'function')
			{
				tmpFlow.openPanel(pNodeHash);
				return;
			}
			if (++tmpTries < 80 && typeof requestAnimationFrame === 'function') { requestAnimationFrame(fOpen); }
		};
		if (typeof requestAnimationFrame === 'function') { requestAnimationFrame(fOpen); } else { setTimeout(fOpen, 300); }
	}

	// "Done": capture the edited board and return to the read-only banner (in the saved display style).
	_exitBoardEdit()
	{
		// Re-entry guard: run the teardown once even if "Done" is pressed twice in quick succession.
		if (this._state().ExitingEdit) { return; }
		this._state().ExitingEdit = true;
		let tmpEdit = this.pict.views[_BOARD_EDIT];
		// Leave the flow's full screen if the editor was expanded (no-op otherwise).
		if (tmpEdit && tmpEdit._FlowView && typeof tmpEdit._FlowView.exitFullscreen === 'function') { tmpEdit._FlowView.exitFullscreen(); }
		// Capture the just-edited board so the read-only view shows the latest -- but guard the fast
		// Edit -> Done race: if the editor has not finished loading and reads back FEWER cards than we
		// opened with, keep the board we already have. Capturing a not-yet-loaded (empty) editor would
		// otherwise blank the banner (and a stray autosave could persist the empty board over real content).
		// A loaded editor (isBoardReady) is always trusted, so clearing the board legitimately still works.
		let tmpCaptured = (tmpEdit && typeof tmpEdit.getBoard === 'function') ? tmpEdit.getBoard() : null;
		let tmpReady = !!(tmpEdit && typeof tmpEdit.isBoardReady === 'function' && tmpEdit.isBoardReady());
		let tmpPriorNodes = (this._state().Board && this._state().Board.Nodes) ? this._state().Board.Nodes.length : 0;
		let tmpCapturedNodes = (tmpCaptured && tmpCaptured.Nodes) ? tmpCaptured.Nodes.length : 0;
		let tmpBoard = (tmpCaptured && (tmpReady || tmpCapturedNodes >= tmpPriorNodes)) ? tmpCaptured : this._state().Board;
		this._state().Board = tmpBoard;
		this._state().BoardEditing = false;
		// Re-present through the SAME path as a fresh load so the empty prompt and the banner reflect what is
		// actually on the board: a board with cards mounts the read-only view AND hides #Profile-Board-Empty;
		// a board you emptied falls back to the "decorate" prompt. The old code mounted the read-only view
		// directly and never toggled the prompt, so a "blank canvas" prompt that was showing on a previously
		// empty board lingered over the freshly decorated banner.
		if (typeof document !== 'undefined') { let tmpBanner = document.getElementById('Profile-Board'); if (tmpBanner) { tmpBanner.innerHTML = ''; } }
		let tmpHasNodes = !!(tmpBoard && tmpBoard.Nodes && tmpBoard.Nodes.length);
		this._presentBoard(tmpBoard, tmpHasNodes, this._isSelf(), this._NavToken);
		this._updateEditBoardButton();
		this._state().ExitingEdit = false;
	}

	// Run a callback after pCount animation frames (fallback to a timeout when rAF is absent), giving the
	// moodboard flow time to finish a render pass before the next step.
	_afterFrames(pCount, fCallback)
	{
		if (typeof requestAnimationFrame !== 'function') { setTimeout(fCallback, 16 * (pCount || 1)); return; }
		let tmpRemaining = pCount || 1;
		let fTick = function () { tmpRemaining -= 1; if (tmpRemaining <= 0) { fCallback(); } else { requestAnimationFrame(fTick); } };
		requestAnimationFrame(fTick);
	}

	// The current personal board id, read by the edit board's Media image source to scope its gallery.
	currentBoardId() { return this._state().BoardID; }

	// Autosave hook wired to Profile-Board-Edit (only your own board is editable).
	onBoardSaved(pBoard)
	{
		let tmpID = this._state().BoardID;
		if (!tmpID || !this._isSelf()) { return; }
		if (this._SaveTimer) { clearTimeout(this._SaveTimer); }
		let tmpSelf = this;
		this._SaveTimer = setTimeout(function ()
		{
			tmpSelf._SaveTimer = null;
			let tmpAPI = tmpSelf.pict.providers.MoodboardAPI;
			if (tmpAPI) { tmpAPI.saveBoard(tmpID, pBoard).catch(() => {}); }
		}, _AUTOSAVE_MS);
	}

	// ── content blocks ───────────────────────────────────────────────────────

	_loadBlocks(pToken)
	{
		let tmpRecord = this._record();
		let tmpAPI = this.pict.providers.ProfileBlockAPI;
		if (!tmpAPI || !tmpRecord.IDUser) { this._state().Blocks = []; this._renderBlocks(); return; }
		tmpAPI.listForUser(tmpRecord.IDUser).then((pBlocks) =>
		{
			if (pToken && pToken !== this._NavToken) { return; }
			this._state().Blocks = Array.isArray(pBlocks) ? pBlocks : [];
			this._renderBlocks();
		});
	}

	_renderBlocks()
	{
		let tmpHost = document.getElementById('Profile-Blocks');
		if (!tmpHost) { return; }
		let tmpBlocks = this._state().Blocks || [];
		let tmpSelf = this._isSelf();
		let tmpAPI = this.pict.providers.ProfileBlockAPI;

		let tmpHTML = '';
		for (let i = 0; i < tmpBlocks.length; i++)
		{
			let tmpBlock = tmpBlocks[i];
			let tmpContent = tmpAPI ? tmpAPI.parseContent(tmpBlock) : {};
			let tmpControls = '';
			if (tmpSelf)
			{
				tmpControls =
					'<div class="pb-controls">'
					+ '<button class="pb-ctl" title="Move up" onclick="_Pict.views[\'Plansheet-Profile\'].moveBlock(' + tmpBlock.IDProfileBlock + ',-1)">&#8593;</button>'
					+ '<button class="pb-ctl" title="Move down" onclick="_Pict.views[\'Plansheet-Profile\'].moveBlock(' + tmpBlock.IDProfileBlock + ',1)">&#8595;</button>'
					+ (tmpBlock.BlockType === 'text' ? '<button class="pb-ctl" title="Edit" onclick="_Pict.views[\'Plansheet-Profile\'].editBlock(' + tmpBlock.IDProfileBlock + ')">Edit</button>' : '')
					+ '<button class="pb-ctl pb-ctl-danger" title="Delete" onclick="_Pict.views[\'Plansheet-Profile\'].deleteBlock(' + tmpBlock.IDProfileBlock + ')">&#215;</button>'
					+ '</div>';
			}
			tmpHTML += '<div class="pb-block pb-block-' + _escapeHTML(tmpBlock.BlockType) + '" data-block-id="' + tmpBlock.IDProfileBlock + '">'
				+ tmpControls
				+ this._blockBodyHTML(tmpBlock, tmpContent)
				+ '</div>';
		}
		if (tmpSelf)
		{
			tmpHTML += '<div class="pb-add">'
				+ '<button class="pb-add-btn" onclick="_Pict.views[\'Plansheet-Profile\'].addBlock(\'text\')">+ Text</button>'
				+ '<button class="pb-add-btn" onclick="_Pict.views[\'Plansheet-Profile\'].addImageBlock()">+ Image</button>'
				+ '<button class="pb-add-btn" onclick="_Pict.views[\'Plansheet-Profile\'].addBlock(\'embed\')">+ Embed</button>'
				+ '</div>';
		}
		tmpHost.innerHTML = tmpHTML;

		// Text blocks render markdown after insertion (same path as About).
		for (let i = 0; i < tmpBlocks.length; i++)
		{
			if (tmpBlocks[i].BlockType !== 'text') { continue; }
			let tmpEl = document.getElementById('PB-Text-' + tmpBlocks[i].IDProfileBlock);
			if (tmpEl)
			{
				let tmpMarkdown = (tmpAPI ? tmpAPI.parseContent(tmpBlocks[i]) : {}).Markdown || '';
				libMarkdownRender.renderInto(this.pict.providers.Content, tmpEl, tmpMarkdown, '<div class="pb-empty">Empty text block.</div>');
			}
		}
	}

	_blockBodyHTML(pBlock, pContent)
	{
		if (pBlock.BlockType === 'text')
		{
			return '<div class="pb-text" id="PB-Text-' + pBlock.IDProfileBlock + '"></div>';
		}
		if (pBlock.BlockType === 'image')
		{
			let tmpURL = pContent.Url || (pContent.IDMedia ? this.pict.providers.MediaAPI.blobURL(pContent.IDMedia) : '');
			if (!tmpURL) { return '<div class="pb-empty">No image.</div>'; }
			let tmpCaption = pContent.Caption ? '<div class="pb-caption">' + _escapeHTML(pContent.Caption) + '</div>' : '';
			return '<img class="pb-image" src="' + _escapeHTML(tmpURL) + '" alt="' + _escapeHTML(pContent.Caption || '') + '">' + tmpCaption;
		}
		if (pBlock.BlockType === 'embed')
		{
			let tmpEmbed = _embedURL(pContent.Url);
			if (tmpEmbed)
			{
				return '<div class="pb-embed"><iframe src="' + _escapeHTML(tmpEmbed) + '" frameborder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>';
			}
			let tmpRaw = String(pContent.Url || '').trim();
			if (/^https:\/\//i.test(tmpRaw))
			{
				return '<a class="pb-link" href="' + _escapeHTML(tmpRaw) + '" target="_blank" rel="noopener noreferrer">' + _escapeHTML(tmpRaw) + '</a>';
			}
			return '<div class="pb-empty">Paste a link (https) to embed.</div>';
		}
		return '';
	}

	addBlock(pType)
	{
		let tmpRecord = this._record();
		let tmpAPI = this.pict.providers.ProfileBlockAPI;
		if (!tmpAPI || !this._isSelf() || !tmpRecord.IDUser) { return; }
		let tmpContent = (pType === 'text') ? { Markdown: '' } : (pType === 'embed' ? { Url: '' } : {});
		let tmpSort = (this._state().Blocks || []).length;
		tmpAPI.createBlock(tmpRecord.IDUser, pType, tmpContent, { SortOrder: tmpSort }).then(() =>
			this._loadBlocks2().then(() =>
			{
				// Drop straight into editing a new text or embed block so it is usable immediately.
				let tmpBlocks = this._state().Blocks || [];
				let tmpNew = tmpBlocks[tmpBlocks.length - 1];
				if (tmpNew && (pType === 'text' || pType === 'embed')) { this.editBlock(tmpNew.IDProfileBlock); }
			}));
	}

	// _loadBlocks that returns a promise (used when an action must continue after the reload).
	_loadBlocks2()
	{
		let tmpRecord = this._record();
		let tmpAPI = this.pict.providers.ProfileBlockAPI;
		if (!tmpAPI || !tmpRecord.IDUser) { return Promise.resolve(); }
		return tmpAPI.listForUser(tmpRecord.IDUser).then((pBlocks) =>
		{
			this._state().Blocks = Array.isArray(pBlocks) ? pBlocks : [];
			this._renderBlocks();
		});
	}

	addImageBlock()
	{
		if (!this._isSelf()) { return; }
		let tmpInput = document.getElementById('Profile-Block-FileInput');
		if (tmpInput) { tmpInput.click(); }
	}

	onBlockImageChosen(pFiles)
	{
		let tmpRecord = this._record();
		let tmpFile = (pFiles && pFiles[0]) ? pFiles[0] : null;
		if (!tmpFile || !this._isSelf() || !this.pict.providers.MediaAPI || !this.pict.providers.ProfileBlockAPI) { return; }
		let tmpSort = (this._state().Blocks || []).length;
		this.pict.providers.MediaAPI.upload('User', tmpRecord.IDUser, tmpFile, {}).then((pRef) =>
		{
			let tmpID = pRef && pRef.IDMedia;
			return this.pict.providers.ProfileBlockAPI.createBlock(tmpRecord.IDUser, 'image', { IDMedia: tmpID }, { SortOrder: tmpSort });
		}).then(() => this._loadBlocks()).catch(() => {});
	}

	editBlock(pID)
	{
		let tmpBlock = (this._state().Blocks || []).find((pB) => pB.IDProfileBlock === pID);
		if (!tmpBlock || !this._isSelf()) { return; }
		let tmpHost = document.querySelector('#Profile-Blocks .pb-block[data-block-id="' + pID + '"]');
		if (!tmpHost) { return; }
		let tmpContent = this.pict.providers.ProfileBlockAPI.parseContent(tmpBlock);
		let tmpEditor = '';
		if (tmpBlock.BlockType === 'text')
		{
			tmpEditor = '<textarea class="pb-editor-text" id="PB-Edit-' + pID + '" placeholder="Write anything (markdown: # heading, **bold**, images, links)">' + _escapeHTML(tmpContent.Markdown || '') + '</textarea>';
		}
		else if (tmpBlock.BlockType === 'embed')
		{
			tmpEditor = '<input class="pb-editor-url" id="PB-Edit-' + pID + '" placeholder="Paste a YouTube/Vimeo or https link" value="' + _escapeHTML(tmpContent.Url || '') + '">';
		}
		tmpHost.innerHTML = '<div class="pb-edit">' + tmpEditor
			+ '<div class="pb-edit-actions">'
			+ '<button class="pb-ctl" onclick="_Pict.views[\'Plansheet-Profile\'].saveBlock(' + pID + ')">Save</button>'
			+ '<button class="pb-ctl pb-ctl-secondary" onclick="_Pict.views[\'Plansheet-Profile\'].cancelEdit()">Cancel</button>'
			+ '</div></div>';
		let tmpField = document.getElementById('PB-Edit-' + pID);
		if (tmpField && tmpField.focus) { tmpField.focus(); }
	}

	saveBlock(pID)
	{
		let tmpBlock = (this._state().Blocks || []).find((pB) => pB.IDProfileBlock === pID);
		let tmpField = document.getElementById('PB-Edit-' + pID);
		if (!tmpBlock || !tmpField) { return; }
		let tmpContent = (tmpBlock.BlockType === 'text') ? { Markdown: tmpField.value } : { Url: tmpField.value };
		this.pict.providers.ProfileBlockAPI.updateBlock(pID, tmpContent).then(() => this._loadBlocks()).catch(() => {});
	}

	cancelEdit() { this._renderBlocks(); }

	deleteBlock(pID)
	{
		if (!this._isSelf()) { return; }
		this.pict.providers.ProfileBlockAPI.remove(pID).then(() => this._loadBlocks()).catch(() => {});
	}

	moveBlock(pID, pDir)
	{
		let tmpBlocks = (this._state().Blocks || []).slice();
		let tmpIndex = tmpBlocks.findIndex((pB) => pB.IDProfileBlock === pID);
		let tmpTarget = tmpIndex + pDir;
		if (tmpIndex < 0 || tmpTarget < 0 || tmpTarget >= tmpBlocks.length) { return; }
		let tmpMoved = tmpBlocks.splice(tmpIndex, 1)[0];
		tmpBlocks.splice(tmpTarget, 0, tmpMoved);
		this._state().Blocks = tmpBlocks;
		this._renderBlocks();
		this.pict.providers.ProfileBlockAPI.reorder(tmpBlocks).catch(() => {});
	}

	// ── navigation ───────────────────────────────────────────────────────────

	editProfile()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showSection === 'function') { tmpApp.showSection('Settings'); }
		let tmpSettings = this.pict.views['Plansheet-UserSettings'];
		if (tmpSettings && typeof tmpSettings.showSection === 'function')
		{
			setTimeout(() => { try { tmpSettings.showSection('about'); } catch (pErr) { /* ignore */ } }, 60);
		}
	}

	community()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showSection === 'function') { tmpApp.showSection('Community'); }
	}

	back() { this.community(); }

	// ── head / about (synchronous DOM fills) ─────────────────────────────────

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		if (typeof document === 'undefined') { return super.onAfterRender(pRenderable, pAddress, pRecord, pContent); }

		let tmpData = this._record();
		let tmpSelf = this._isSelf(tmpData);

		let tmpAvatarEl = document.getElementById('Profile-Avatar');
		if (tmpAvatarEl) { tmpAvatarEl.innerHTML = libAvatarHelper.avatarHTML(tmpData, { Size: 72 }); }

		let tmpNameEl = document.getElementById('Profile-Name');
		if (tmpNameEl) { tmpNameEl.textContent = tmpData.DisplayName || tmpData.Email || 'Profile'; }

		let tmpTitleEl = document.getElementById('Profile-Title');
		if (tmpTitleEl)
		{
			tmpTitleEl.textContent = tmpData.Title || '';
			tmpTitleEl.style.display = tmpData.Title ? 'block' : 'none';
		}

		let tmpActionsEl = document.getElementById('Profile-Actions');
		if (tmpActionsEl)
		{
			let tmpButtons = '<button class="cst-profile-btn-secondary" onclick="_Pict.views[\'Plansheet-Profile\'].community()">Community</button>';
			if (tmpSelf)
			{
				// "Edit board" / "Done" toggles the banner between its saved display style (read-only) and the
				// editable canvas, in place. The board's own toolbar carries the style + view-area controls.
				tmpButtons += '<button class="cst-profile-btn-secondary" id="Profile-Edit-Board" onclick="_Pict.views[\'Plansheet-Profile\'].toggleBoardEdit()">' + (this._state().BoardEditing ? 'Done' : 'Edit board') + '</button>';
				tmpButtons += '<button class="cst-profile-btn" onclick="_Pict.views[\'Plansheet-Profile\'].editProfile()">Edit profile</button>';
			}
			tmpActionsEl.innerHTML = tmpButtons;
		}

		let tmpAboutEl = document.getElementById('Profile-About');
		if (tmpAboutEl)
		{
			let tmpEmpty = '<div class="cst-profile-empty">' + (tmpSelf ? 'You have not written an About Me yet. Edit your profile to add one.' : 'Nothing here yet.') + '</div>';
			libMarkdownRender.renderInto(this.pict.providers.Content, tmpAboutEl, tmpData.AboutMe || '', tmpEmpty);
		}

		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewProfile;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Profile',
	DefaultRenderable: 'Profile-Display',
	AutoRender: false,
	CSS: /*css*/`
		.cst-profile { max-width: 980px; margin: 0 auto; padding: 0 0 48px; height: 100%; overflow: auto; box-sizing: border-box; position: relative; }
		.cst-profile-board { height: 340px; position: relative; background: var(--theme-color-background-secondary, #f4f6f9); border-bottom: 1px solid var(--theme-color-border-default, #e3e6ec); }
		/* Presentation styles: a jumbotron banner (its height is the scaled view-area frame) reads as a
		   hero band; a background fills the banner as a backdrop. The host JS sets the inline height. */
		.cst-profile-board.cst-board-jumbotron, .cst-profile-board.cst-board-background { background: var(--theme-color-background-panel, #fff); }
		/* Backdrop display style (view mode only): the board is a full-bleed layer behind the whole profile
		   column; the head / blocks / About render on top of it, and the gear "Backdrop top margin" offsets
		   the board's focal content downward so it clears the header row. The stage gives the absolute board
		   a content-height containing block (so the backdrop spans all the content, not just the viewport). */
		.cst-profile-stage { position: relative; }
		/* The backdrop fills at least the viewport so a sparse profile still shows the full board art, and
		   grows with taller content. */
		.cst-profile-backdrop .cst-profile-stage { min-height: 100%; }
		.cst-profile-backdrop .cst-profile-board { position: absolute; inset: 0; height: auto !important; z-index: 0; border-bottom: none; pointer-events: none; }
		/* The board element carries the backdrop color (set inline from the moodboard's resolved color); the
		   flow's own container fills go transparent so that color shows -- the flow SVG fill does not
		   composite reliably as an absolute layer. The cards still render on top. */
		.cst-profile-backdrop .cst-profile-board .pict-flow-container,
		.cst-profile-backdrop .cst-profile-board .pict-flow-svg-container { background: transparent !important; }
		.cst-profile-backdrop .cst-profile-inner { position: relative; z-index: 1; }
		/* "Edit board" toggle on your own board (overlaid top-left, opposite the viewer bar). Lives outside
		   #Profile-Board so the board's render does not wipe it. */
		/* Slim prompt shown instead of a tall empty canvas on your own undecorated board. */
		.cst-profile-board-empty { align-items: center; justify-content: center; gap: 14px; height: 96px; background: var(--theme-color-background-secondary, #f4f6f9); border-bottom: 1px solid var(--theme-color-border-default, #e3e6ec); }
		.cst-profile-board-empty-text { color: var(--theme-color-text-secondary, #5b6376); font-size: 0.95em; }
		.cst-profile-inner { padding: 0 32px; }
		.cst-profile-head { display: flex; align-items: center; gap: 18px; padding: 20px 0; border-bottom: 1px solid var(--theme-color-border-default, #e3e6ec); }
		/* The header (avatar + name) is its own section below the board banner -- they must not overlap.
		   No negative margin pulling the avatar up into the board. */
		.cst-profile-avatar { flex: 0 0 auto; border-radius: 50%; background: var(--theme-color-background-panel, #fff); }
		.cst-profile-id { flex: 1 1 auto; min-width: 0; }
		.cst-profile-name { margin: 0; font-size: 1.6em; color: var(--theme-color-text-primary, #1d2230); }
		.cst-profile-title { margin-top: 3px; font-size: 0.95em; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-profile-actions { display: flex; gap: 8px; flex: 0 0 auto; }
		.cst-profile-btn { padding: 8px 16px; border: 1px solid var(--theme-color-brand-primary, #3b6fd4); border-radius: 8px; background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; cursor: pointer; font-size: 0.85em; }
		.cst-profile-btn:hover { filter: brightness(1.05); }
		.cst-profile-btn-secondary { padding: 8px 16px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); cursor: pointer; font-size: 0.85em; }

		/* Content blocks */
		.cst-profile-blocks { padding: 22px 0 8px; display: flex; flex-direction: column; gap: 16px; }
		.pb-block { position: relative; border: 1px solid var(--theme-color-border-default, #e7eaf0); border-radius: 12px; padding: 16px 18px; background: var(--theme-color-background-panel, #fff); }
		.pb-controls { position: absolute; top: 8px; right: 8px; display: none; gap: 4px; }
		.pb-block:hover .pb-controls { display: flex; }
		.pb-ctl { border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff); border-radius: 6px; padding: 3px 8px; cursor: pointer; font-size: 12px; color: var(--theme-color-text-primary, #1d2230); }
		.pb-ctl:hover { background: var(--theme-color-background-hover, #f2f4f8); }
		.pb-ctl-danger { color: #b3261e; }
		.pb-ctl-secondary { color: var(--theme-color-text-secondary, #5b6376); }
		.pb-text { line-height: 1.6; color: var(--theme-color-text-primary, #1d2230); }
		.pb-text img { max-width: 100%; border-radius: 8px; }
		.pb-image { max-width: 100%; border-radius: 10px; display: block; }
		.pb-caption { margin-top: 6px; font-size: 0.85em; color: var(--theme-color-text-secondary, #5b6376); }
		.pb-embed { position: relative; padding-top: 56.25%; border-radius: 10px; overflow: hidden; }
		.pb-embed iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
		.pb-link { color: var(--theme-color-brand-primary, #3b6fd4); word-break: break-all; }
		.pb-empty { color: var(--theme-color-text-secondary, #8a93a5); font-style: italic; }
		.pb-add { display: flex; gap: 8px; padding-top: 4px; }
		.pb-add-btn { border: 1px dashed var(--theme-color-border-default, #cfd6e2); background: transparent; border-radius: 8px; padding: 8px 14px; cursor: pointer; color: var(--theme-color-text-secondary, #5b6376); font-size: 0.9em; }
		.pb-add-btn:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-brand-primary, #3b6fd4); }
		.pb-edit { display: flex; flex-direction: column; gap: 8px; }
		.pb-editor-text { width: 100%; min-height: 120px; box-sizing: border-box; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; padding: 10px; font: inherit; line-height: 1.5; resize: vertical; }
		.pb-editor-url { width: 100%; box-sizing: border-box; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; padding: 9px 10px; font: inherit; }
		.pb-edit-actions { display: flex; gap: 8px; }

		.cst-profile-about-wrap { padding-top: 26px; }
		.cst-profile-about-h { margin: 0 0 12px; font-size: 1.05em; color: var(--theme-color-text-secondary, #5b6376); text-transform: uppercase; letter-spacing: 0.04em; }
		.cst-profile-about { color: var(--theme-color-text-primary, #1d2230); line-height: 1.6; }
		.cst-profile-about img { max-width: 100%; border-radius: 8px; }
		.cst-profile-empty { padding: 16px; border: 1px dashed var(--theme-color-border-default, #d8dde6); border-radius: 10px; color: var(--theme-color-text-secondary, #5b6376); font-size: 0.9em; }
	`,
	Templates:
	[
		{
			Hash: 'Profile-Display',
			Template: /*html*/`
<div class="cst-profile">
	<div class="cst-profile-stage">
	<div class="cst-profile-board" id="Profile-Board"></div>
	<div class="cst-profile-board-empty" id="Profile-Board-Empty" style="display:none">
		<div class="cst-profile-board-empty-text">Your board is a blank canvas.</div>
		<button class="cst-profile-btn" onclick="_Pict.views['Plansheet-Profile'].startDecorating()">Decorate your board</button>
	</div>
	<div class="cst-profile-inner">
		<div class="cst-profile-head">
			<span class="cst-profile-avatar" id="Profile-Avatar"></span>
			<div class="cst-profile-id">
				<h1 class="cst-profile-name" id="Profile-Name"></h1>
				<div class="cst-profile-title" id="Profile-Title"></div>
			</div>
			<div class="cst-profile-actions" id="Profile-Actions"></div>
		</div>
		<div class="cst-profile-blocks" id="Profile-Blocks"></div>
		<div class="cst-profile-about-wrap">
			<h2 class="cst-profile-about-h">About</h2>
			<div class="cst-profile-about" id="Profile-About"></div>
		</div>
	</div>
	</div>
	<input type="file" accept="image/*" style="display:none" id="Profile-Block-FileInput"
		onchange="_Pict.views['Plansheet-Profile'].onBlockImageChosen(this.files); this.value='';">
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'Profile-Display',
			TemplateHash: 'Profile-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
